/* AppraisalComponents/hooks/useFasteningCascade.js
 * Phase C · C4b Fastening extraction (2026-02c · proof-pattern).
 *
 * Section-scoped cascade for the Fastening work-type. Extracted from
 * AppraisalComponentsTab.js:2044-2115. Owns the useEffect that reads
 * engine `rate_picks.Fastening` and stamps the winning rate row onto
 * every cascadable Fastening line (blank / diagram-pushed / not
 * operator-overridden). Also sets first-time defaults for Units +
 * Screw Rate + m² source.
 *
 * Contract:
 *   Reads:  loading, quoteType, rates.Fastening, groups.Fastening,
 *           roofM2, engineRatePicks
 *   Writes: setGroups(prev => ...) — mutates only prev.Fastening
 *
 * All state stays in the parent · this hook takes state slices as
 * arguments + a setter callback. Zero React state of its own.
 */
import { useEffect } from 'react';

export function useFasteningCascade({
  loading,
  quoteType,
  rates,
  groupsFasteningLength,   // groups.Fastening?.length — cheap dep
  roofM2,
  engineRatePicks,
  setGroups,
}) {
  useEffect(() => {
    if (loading) return;
    if (!quoteType) return;
    const fasteningRates = rates.Fastening || [];
    if (!fasteningRates.length) return;

    const findByName = (...needles) => fasteningRates.find(r => {
      const tn = String(r.type_name || '').toLowerCase();
      return needles.every(n => tn.includes(n.toLowerCase()));
    });

    // C3c-β · Fastening picks are owned entirely by engine FST-* rules.
    // Rules cover Reroof LongRun (Corrugate → 50mm · Ribbed → 65mm ·
    // Default → 65mm) and Rescrew Iron (any profile → 12*65). Tile
    // rescrew requires per-row context.row_position and stays blank
    // for operator pick. If engine returns no pick for this anchor
    // tuple, we leave rows blank rather than guessing.
    let winner = null;
    const enginePick = engineRatePicks?.Fastening;
    if (enginePick && Array.isArray(enginePick.tokens) && enginePick.tokens.length) {
      winner = findByName(...enginePick.tokens);
    }
    if (!winner) return;

    // Iter152 — Default Units to the appraisal's roof_m2 (the most
    // common case). Operator can flip the m² source picker on each row
    // to Face Pin / Sum of Face Pins, or hand-edit Units (↻ Reset chip
    // surfaces on the strip when current value differs from source).
    const defaultUnits = Number(roofM2) || 0;

    setGroups(prev => {
      const lines = prev.Fastening || [];
      if (!lines.length) return prev;
      let touched = false;
      const next = lines.map(l => {
        const isDiagramPush = l.import_source === 'roof_diagram';
        const typeMatchesRateRow = !!l.type_name
          && fasteningRates.some(r => r.type_name === l.type_name);
        const cascadable = !l.type_name || (isDiagramPush && !typeMatchesRateRow);
        // Operator override is sacred — never overwrite type or rates.
        if (!cascadable || l.override) return l;
        const patch = {};
        if (l.type_name !== winner.type_name) {
          patch.type_name = winner.type_name;
          if (!l.buy_cost && winner.buy_cost)               patch.buy_cost        = winner.buy_cost;
          if (!l.materials_rate && winner.materials_rate)   patch.materials_rate  = winner.materials_rate;
          if (!l.labour_rate && winner.labour_rate)         patch.labour_rate     = winner.labour_rate;
          if (!l.contractor_rate && winner.contractor_rate) patch.contractor_rate = winner.contractor_rate;
          if (!l.supplier && winner.supplier)               patch.supplier        = winner.supplier;
          if (!l.supplier_code && winner.supplier_code)     patch.supplier_code   = winner.supplier_code;
          if (!l.materials_desc && winner.materials_desc)   patch.materials_desc  = winner.materials_desc;
          if (!l.labour_details && winner.labour_details)   patch.labour_details  = winner.labour_details;
        }
        // First-time Units + ScrewRate + m² source defaults. Only fires
        // when current Units (length) is 0 → never tramples hand-edits.
        if (!Number(l.length) && defaultUnits > 0) patch.length     = defaultUnits;
        if (!Number(l.quantity))                   patch.quantity   = 1;
        if (!Number(l.screw_rate))                 patch.screw_rate = 7;
        if (!l.m2_source)                          patch.m2_source  = 'roof_details';
        if (Object.keys(patch).length === 0) return l;
        touched = true;
        return { ...l, ...patch };
      });
      return touched ? { ...prev, Fastening: next } : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, rates, quoteType, roofM2, groupsFasteningLength, engineRatePicks]);
}
